import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { BACKEND } from "../lib/api";
import { fmtDate } from "../components/common";
import { SeverityBadge, SEVERITY } from "../components/SeverityBadge";
import { Logo } from "../components/Logo";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from "recharts";
import {
  ArrowLeft, DownloadSimple, Sparkle, ShieldCheck, Fingerprint,
} from "@phosphor-icons/react";

const SECTIONS = [
  ["summary", "Executive Summary"],
  ["risk", "Risk Matrix"],
  ["scope", "Scope & RoE"],
  ["methodology", "Methodology"],
  ["findings", "Findings"],
  ["remediation", "Remediation"],
];

export default function ReportView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);

  useEffect(() => {
    api.get(`/reports/${id}`).then((res) => setR(res.data)).catch(() => navigate("/reports"));
  }, [id, navigate]);

  const downloadPdf = () => {
    const token = localStorage.getItem("sx_token");
    window.open(`${BACKEND}/api/reports/${id}/pdf?token=${token}`, "_blank");
  };

  if (!r) return <div className="text-slate-400">Loading report…</div>;

  const total = Object.values(r.stats).reduce((a, b) => a + b, 0) || 1;
  const bars = ["critical", "high", "medium", "low", "info"].map((k) => ({
    name: SEVERITY[k].label, value: r.stats[k] || 0, color: SEVERITY[k].color,
  }));
  const posture = r.stats.critical ? "CRITICAL" : r.stats.high ? "HIGH" : r.stats.medium ? "MODERATE" : "LOW";
  const postureColor = SEVERITY[posture === "CRITICAL" ? "critical" : posture === "HIGH" ? "high" : posture === "MODERATE" ? "medium" : "low"].color;

  return (
    <div className="fade-up max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <Button data-testid="report-back-button" variant="ghost" onClick={() => navigate("/reports")}
          className="text-slate-400 hover:text-white hover:bg-slate-800">
          <ArrowLeft size={18} className="mr-1.5" /> Back
        </Button>
        <Button data-testid="report-download-pdf" onClick={downloadPdf}
          className="bg-blue-600 hover:bg-blue-500 text-white">
          <DownloadSimple size={18} weight="bold" className="mr-1.5" /> Download branded PDF
        </Button>
      </div>

      {/* Cover */}
      <Card className="relative overflow-hidden bg-[#0b1220] border-[#1e293b] rounded-lg p-8 mb-4">
        <div className="absolute right-0 top-0 h-full w-1/2 opacity-20"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1683322499436-f4383dd59f5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800)", backgroundSize: "cover", backgroundPosition: "center", maskImage: "linear-gradient(to left, black, transparent)", WebkitMaskImage: "linear-gradient(to left, black, transparent)" }} />
        <div className="relative">
          <div className="flex items-center gap-3 mb-6">
            <Logo size={40} />
            <div>
              <div className="font-head font-extrabold text-white text-xl">SoverenX Systems</div>
              <div className="overline text-slate-500">Penetration Test Report</div>
            </div>
          </div>
          <h1 className="font-head text-3xl md:text-4xl font-bold tracking-tight text-white">{r.engagement_name}</h1>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-sm">
            <Meta label="Client" value={r.client_name} />
            <Meta label="Assessment" value={r.scan_type} />
            <Meta label="Generated" value={fmtDate(r.generated_at)} />
            <Meta label="Residency" value="Australia East" />
          </div>
          <div className="mt-6 inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold"
            style={{ backgroundColor: `${postureColor}18`, color: postureColor, border: `1px solid ${postureColor}55` }}>
            <ShieldCheck size={16} weight="fill" /> Overall posture: {posture}
          </div>
        </div>
      </Card>

      {/* Executive summary */}
      <Section id="summary" title="Executive Summary"
        badge={r.ai_generated && <span className="inline-flex items-center gap-1 text-xs text-blue-400"><Sparkle size={13} weight="fill" /> AI-assisted</span>}>
        <p className="text-slate-300 leading-relaxed whitespace-pre-line">{r.executive_summary}</p>
      </Section>

      {/* Risk matrix */}
      <Section id="risk" title="Risk Matrix & Severity Breakdown">
        <div className="grid md:grid-cols-2 gap-6 items-center">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={bars}>
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} stroke="#1e293b" />
              <YAxis tick={{ fill: "#64748b", fontSize: 11 }} stroke="#1e293b" allowDecimals={false} />
              <Tooltip cursor={{ fill: "#1e293b55" }}
                contentStyle={{ background: "#0b1220", border: "1px solid #1e293b", borderRadius: 8, color: "#fff" }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {bars.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="space-y-2">
            {bars.map((b) => (
              <div key={b.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-300">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: b.color }} /> {b.name}
                </span>
                <span className="font-mono text-white">{b.value} <span className="text-slate-500">({Math.round(b.value / total * 100)}%)</span></span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Scope & RoE */}
      <Section id="scope" title="Scope & Rules of Engagement">
        {r.consent ? (
          <div className="space-y-3 text-sm">
            <Field label="Scope" value={r.consent.scope} />
            <Field label="Authorized targets" value={r.consent.authorized_targets.join(", ")} mono />
            <Field label="Time window" value={r.consent.time_window} />
            <div className="rounded-md bg-[#020817] border border-[#1e293b] p-3">
              <div className="flex items-center gap-2 overline text-blue-400 mb-1"><Fingerprint size={14} /> Consent record</div>
              <div className="text-slate-300">Signed by <b>{r.consent.signatory_name}</b> ({r.consent.signatory_role}) · {fmtDate(r.consent.timestamp)}</div>
              <div className="font-mono text-xs text-slate-500 mt-1">ID {r.consent.id}</div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-300">
            <Field label="Target" value={`${r.target.name} (${r.target.value})`} mono />
            <p className="mt-2 text-slate-400">No exploitation was performed; authorized discovery / vulnerability assessment only.</p>
          </div>
        )}
      </Section>

      {/* Methodology */}
      <Section id="methodology" title="Methodology">
        <p className="text-slate-300 leading-relaxed">{r.methodology}</p>
      </Section>

      {/* Findings */}
      <Section id="findings" title={`Findings (${r.findings.length})`}>
        <div className="space-y-3">
          {r.findings.length === 0 && <p className="text-slate-400 text-sm">No findings identified.</p>}
          {r.findings.map((f, i) => (
            <div key={f.id} className="rounded-md border border-[#1e293b] p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="font-medium text-white">{i + 1}. {f.title}</div>
                <SeverityBadge severity={f.severity} cvss={f.cvss} />
              </div>
              <div className="font-mono text-xs text-blue-300 mt-1">{f.cve} · {f.host}:{f.port} ({f.service})</div>
              <p className="text-sm text-slate-300 mt-2">{f.description}</p>
              <div className="mt-2 rounded bg-[#020817] border border-[#1e293b] p-2 font-mono text-xs text-emerald-300">{f.evidence}</div>
              <p className="text-sm text-blue-200 mt-2"><b className="text-blue-400">Fix:</b> {f.remediation}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Remediation */}
      <Section id="remediation" title="Remediation Recommendations"
        badge={r.ai_generated && <span className="inline-flex items-center gap-1 text-xs text-blue-400"><Sparkle size={13} weight="fill" /> AI-assisted</span>}>
        <p className="text-slate-300 leading-relaxed whitespace-pre-line">{r.remediation}</p>
      </Section>
    </div>
  );
}

function Section({ id, title, children, badge }) {
  return (
    <Card id={id} data-testid={`report-section-${id}`} className="bg-[#0f172a] border-[#1e293b] rounded-lg p-6 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-head text-xl font-semibold text-white tracking-tight">{title}</h2>
        {badge}
      </div>
      {children}
    </Card>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="overline text-slate-500">{label}</div>
      <div className="text-white capitalize mt-0.5">{value}</div>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <span className="overline text-slate-500">{label}: </span>
      <span className={`text-slate-200 ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}
